import { ApiError } from '@clockwise/shared';

/** One of the share dialog's lookups that did not come back. */
export interface FailedLookup {
  /** Plural noun for what was being listed — "people", "service roles". */
  what: string;
  /** The permission that listing it needs, for when the failure is a refusal. */
  permission: string;
  error: unknown;
}

/**
 * What the share dialog says when it cannot load who there is to share with.
 *
 * EACH FAILURE IS EXPLAINED AS WHAT IT IS. The lookups fail independently and
 * for different reasons: one may be refused for want of a permission while
 * another simply errored. Folding them into one sentence blames a permission
 * for a failure that granting it will not fix — and sends somebody to an
 * administrator about a server error.
 *
 * `null` when nothing failed.
 */
export function lookupFailureMessage(
  failed: readonly FailedLookup[],
): { heading: string; detail: string } | null {
  if (failed.length === 0) return null;
  const isRefusal = (l: FailedLookup) =>
    l.error instanceof ApiError && l.error.code === 'FORBIDDEN';
  const refused = failed.filter(isRefusal);
  const broken = failed.filter((l) => !isRefusal(l));

  const sentences: string[] = [];
  if (refused.length > 0) {
    sentences.push(
      `Listing ${listOf(refused.map((l) => l.what))} needs the ${listOf(
        refused.map((l) => `“${l.permission}”`),
      )} ${refused.length === 1 ? 'permission' : 'permissions'}.`,
    );
  }
  for (const l of broken) {
    // The server's own words when it gave any; they are the only clue there is.
    const reason = l.error instanceof ApiError ? l.error.message : 'Please try again.';
    sentences.push(`${capitalize(l.what)} failed to load: ${endSentence(reason)}`);
  }
  // True whatever failed: that switch names nobody, so it needs no lookup.
  sentences.push('You can still share with everyone.');

  return {
    heading: `Couldn’t load ${listOf(failed.map((l) => l.what))}.`,
    detail: sentences.join(' '),
  };
}

/** `"people"`, `"people and service roles"`, `"people, service roles and system roles"`. */
function listOf(parts: readonly string[]): string {
  if (parts.length <= 1) return parts[0] ?? '';
  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
}

const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
const endSentence = (s: string) => (/[.!?]$/.test(s) ? s : `${s}.`);
